import React from "react";
import { Box, CircularProgress, Divider, IconButton, Stack, Typography, Chip, Tooltip } from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useEffect, useRef, useState } from "react";
import { EventItemDisplay } from "./EventItemDisplay";
import { Button } from "@mui/material";
import CreateApi from "../../shared/Settings/appSettings";
import { useCache } from "../../shared/contexts/CacheContext";
import { RoundItemDisplay } from "./RoundItemDisplay";
import { NewChallengeDialog } from "./NewChallengeDialog";
import { NewSideGameDialog } from "./NewSideGameDialog";
import { NewEventItemDialog } from "./NewEventItemDialog";
import { EventTeeSheet } from "./EventTeeSheet";
import { TabPanel, RoundButton } from "./TabComponents";
import { JSONDisplayComponent } from "./JSONDisplayComponent";
import { NewEventInfoDialog } from "./NewEventInfoDialog";
import { PushNotificationDialog } from "./PushNotificationDialog";
import { TeeSheetPopout } from "./TeeSheetPopout";
import { RegistrationTab } from "./RegistrationTab";
import { RegistrationPopout } from "./RegistrationPopout";
// Used by commented event-view-sidebar (Delete); keep available when sidebar is restored
// import { StyledIconButton } from "../../shared/Components/StyledIconButton";
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from "react-oidc-context";
import { useTBContext } from "../Contexts/useTBStates";
import { eventDataLayout as defaultEventDataLayout } from "../../shared/config/EventDataLayout";
import { roundDataLayout as defaultRoundDataLayout } from "../../shared/config/RoundDataLayout";
import type { EventDataLayout, RoundDataLayout } from "@moment-golf/types";
import { resolveRivalryConfig } from "@moment-golf/types";
import { RegistrationFormPopout } from "./RegistrationFormPopout";
import { RegistrationDisplayInfoPopout } from "./RegistrationDisplayInfoPopout";
import { FlightingTab } from "./Event Pages/FlightingTab";
import { EventDetailsTab } from "./Event Pages/EventDetailsTab";
import { RoundDetailsTab } from "./Event Pages/RoundDetailsTab";
import { CreateEventScores } from "./Functions/CreateEventScores";
import { hasAnyPlayerStartedAnyRound, hasAnyPlayerStartedRound } from "./Functions/cascadeLockedFields";
import {
    applyUniformStartHoleToViewEventData,
    clampStartHole,
    effectiveRoundBool,
} from "./Functions/applyUniformStartHoleToViewEventData";
import { SideGameOptions } from "./Side Games/SideGames";
import { StyledAutocompleteInput } from "../../shared/Components/StyledAutocompleteInput";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ScienceIcon from '@mui/icons-material/Science';
import ClearIcon from '@mui/icons-material/Clear';
import { ChallengeOptions } from "./Side Games/Challenges";
import { localDateStringToUTC, utcToLocalDateString, isDateOnlyString, isDevEnvironment, isOpsActiveRegistration } from "@moment-golf/shared";
import { EventInfoEditors } from "./EventInfoEditors";

const EVENT_DATE_KEYS = ['startDate', 'endDate', 'regCloseDate', 'regOpenDate', 'earlyRegCutoffDate', 'regCutoffDate', 'canceledAt'];
const ROUND_DATE_KEYS = ['roundDate', 'canceledAt'];

/** Convert date-only strings (YYYY-MM-DD) to UTC ISO strings before sending to server */
function convertDatesToUTC(payload: Record<string, unknown>): Record<string, unknown> {
    const result = { ...payload };
    for (const key of EVENT_DATE_KEYS) {
        const val = result[key];
        if (isDateOnlyString(val)) {
            result[key] = localDateStringToUTC(val);
        }
    }
    if (Array.isArray(result.rounds)) {
        result.rounds = result.rounds.map((round: any) => {
            const roundCopy = { ...round };
            for (const key of ROUND_DATE_KEYS) {
                const val = roundCopy[key];
                if (isDateOnlyString(val)) {
                    roundCopy[key] = localDateStringToUTC(val);
                }
            }
            return roundCopy;
        });
    }
    return result;
}

interface TournamentEditorProps {
    appDims: any;
    compHeights: any;
    /* refreshRoundData: () => void; */
    returnToManager: React.Dispatch<React.SetStateAction<any>>;
    deleteEvent: () => void;
}

export const TournamentEditor = ({
    appDims, 
    compHeights,
    /* refreshRoundData,  */
    returnToManager,
    deleteEvent
}: TournamentEditorProps) => {

    const auth = useAuth();
    const api = CreateApi();

    const { getData, setData, clearData } = useCache();

    const {eventData, setEventData,
        viewEvent, setViewEvent,
        viewEventData, setViewEventData,
        editMode, setEditMode,
        setEventTab,
        eventTabValue, setEventTabValue,
        setRoundTabValue,
        roundValue, setRoundValue,
        forceUpdate, setForceUpdate,
        factor, appTheme,
        originalViewEvent,
        originalViewEventData,
        originalRegistrations,
        optionsLoaded,
        viewGroup,
        setAppSection,
        setGroupTabValue,

      } = useTBContext();

    const [isLoading, setIsLoading] = useState(true);
    const [eventDataLayout, setEventDataLayout] = useState<EventDataLayout>(defaultEventDataLayout);
    const [roundDataLayout, setRoundDataLayout] = useState<RoundDataLayout>(defaultRoundDataLayout);
    const [allTeeTimesValid, setAllTeeTimesValid] = useState(false);

    // Cascade refs — track previous values to compute deltas when fields change in edit mode
    const prevStartDateRef = useRef<string | null>(null);
    const prevRoundStartingTimesRef = useRef<Record<number, string>>({});
    const prevRoundTeeTimeGapsRef = useRef<Record<number, number>>({});

    useEffect(() => {
        if (viewEvent) {
            setIsLoading(false);
        }
    }, [viewEvent]);

    useEffect(() => {
        if (viewEventData.registrations && (!originalRegistrations.current || originalRegistrations.current.length === 0)) {
            originalRegistrations.current = JSON.parse(JSON.stringify(viewEventData.registrations));
        }

        if (viewEventData.registrations) {
            console.log('Checking if Registrations are valid');
            const allRegistrationsValid = viewEventData.registrations
                .filter((registration: any) => isOpsActiveRegistration(registration))
                .every((registration: any) => 
                    registration?.teeTimes?.every((teeTime: any) => 
                        teeTime?.teeTime !== undefined && teeTime?.teeTime !== null && teeTime?.teeTime !== ''
                    ) &&
                    registration?.teeTimes?.length === viewEvent?.totalRounds
                );

            if (!allRegistrationsValid) {
                console.log('Registrations are not valid');
                setAllTeeTimesValid(false);

            } else {
                console.log('Registrations are valid');
                setAllTeeTimesValid(true);
            }
        }

    }, [viewEventData?.registrations]);

    useEffect(() => {
        if (!viewEvent) return;
        const courses = viewEventData?.courses as Array<{ courseName: string; _id: string }> | undefined;
        if (viewEvent.courseName && courses?.length) {
            const course = courses.find((c) => c.courseName === viewEvent.courseName);
            if (course) viewEvent.courseID = course._id;
        }
    }, [viewEvent?.courseName, viewEventData?.courses]);

    useEffect(() => {
        if (!viewEvent) return;
        const round = viewEvent.rounds[roundValue - 1];
        const coursesOption = optionsLoaded.find((item: any) => item.keyName === 'courses') as { options: Array<{ courseName: string; _id: string }> } | undefined;
        if (round?.courseName && coursesOption?.options?.length) {
            const course = coursesOption.options.find((c) => c.courseName === round.courseName);
            if (course) (round as { courseID?: string }).courseID = course._id;
        }
    }, [viewEvent?.rounds, roundValue, optionsLoaded]);

    // Reset cascade refs whenever edit mode is enabled so deltas are relative to the state at edit start
    useEffect(() => {
        if (!editMode || !viewEvent) return;
        prevStartDateRef.current = viewEvent.startDate as unknown as string;
        prevRoundStartingTimesRef.current = Object.fromEntries(
            viewEvent.rounds.map((r: any, i: number) => [i, r.startingTime])
        );
        prevRoundTeeTimeGapsRef.current = Object.fromEntries(
            viewEvent.rounds.map((r: any, i: number) => [i, r.teeTimeGap])
        );
    }, [editMode]);

    // Cascade effect 1 — event startDate change: shift all round.roundDate and all registration.teeTimes by the same day delta
    useEffect(() => {
        if (!editMode || !viewEvent) return;
        const newDateStr = viewEvent.startDate as unknown as string;
        if (!prevStartDateRef.current || prevStartDateRef.current === newDateStr) return;

        // Normalize both dates to UTC midnight before computing delta (avoids local-timezone shifts)
        const prevDateOnly = utcToLocalDateString(prevStartDateRef.current);
        const newDateOnly = isDateOnlyString(newDateStr) ? newDateStr : utcToLocalDateString(newDateStr);
        const prevMs = new Date(localDateStringToUTC(prevDateOnly)).getTime();
        const newMs = new Date(localDateStringToUTC(newDateOnly)).getTime();
        const msDelta = newMs - prevMs;
        if (msDelta === 0) return;

        prevStartDateRef.current = newDateStr;

        const updatedRounds = viewEvent.rounds.map((round: any) => {
            if (!round.roundDate) return round;
            return { ...round, roundDate: new Date(new Date(round.roundDate).getTime() + msDelta).toISOString() };
        });

        const updatedRegistrations = viewEventData.registrations.map((reg: any) => {
            if (!reg.teeTimes?.length) return reg;
            return {
                ...reg,
                teeTimes: reg.teeTimes.map((tt: any) => ({
                    ...tt,
                    teeTime: tt.teeTime ? new Date(new Date(tt.teeTime).getTime() + msDelta).toISOString() : tt.teeTime,
                })),
            };
        });

        setViewEvent({ ...viewEvent, rounds: updatedRounds });
        setViewEventData({ ...viewEventData, registrations: updatedRegistrations });
    }, [viewEvent?.startDate]);

    // Cascade effect 2 — round startingTime change: shift stored tee times for that round by the minute delta
    useEffect(() => {
        if (!editMode || !viewEvent) return;
        const roundIndex = roundValue - 1;
        const round = viewEvent.rounds[roundIndex];
        if (!round) return;
        const newTime = round.startingTime as string;
        const oldTime = prevRoundStartingTimesRef.current[roundIndex];
        if (!oldTime || oldTime === newTime) return;

        const parseMinutes = (t: string) => {
            const [h, m] = t.split(':').map(Number);
            return h * 60 + m;
        };
        const minuteDelta = parseMinutes(newTime) - parseMinutes(oldTime);
        if (minuteDelta === 0) return;

        prevRoundStartingTimesRef.current = { ...prevRoundStartingTimesRef.current, [roundIndex]: newTime };

        const msDelta = minuteDelta * 60000;
        const updatedRegistrations = viewEventData.registrations.map((reg: any) => {
            if (!reg.teeTimes?.length) return reg;
            return {
                ...reg,
                teeTimes: reg.teeTimes.map((tt: any) => {
                    if (tt.round !== roundValue || !tt.teeTime) return tt;
                    return { ...tt, teeTime: new Date(new Date(tt.teeTime).getTime() + msDelta).toISOString() };
                }),
            };
        });

        setViewEventData({ ...viewEventData, registrations: updatedRegistrations });
    }, [viewEvent?.rounds[roundValue - 1]?.startingTime]);

    // Cascade effect 3 — round teeTimeGap change: recalculate tee times using slot-index approach
    useEffect(() => {
        if (!editMode || !viewEvent) return;
        const roundIndex = roundValue - 1;
        const round = viewEvent.rounds[roundIndex];
        if (!round) return;
        const newGap: number = Number(round.teeTimeGap);
        const oldGap: number = Number(prevRoundTeeTimeGapsRef.current[roundIndex]);
        if (!oldGap || oldGap === newGap || newGap <= 0) return;

        prevRoundTeeTimeGapsRef.current = { ...prevRoundTeeTimeGapsRef.current, [roundIndex]: newGap };

        // Compute the round's base datetime in UTC (matching how tee times are stored)
        const getRoundBaseMs = (): number => {
            const baseDate = round.roundDate
                ? new Date(round.roundDate)
                : (() => {
                    const d = new Date(viewEvent.startDate);
                    d.setUTCDate(d.getUTCDate() + roundIndex);
                    return d;
                })();
            if (round.startingTime) {
                const [h, m] = round.startingTime.split(':').map(Number);
                return Date.UTC(baseDate.getUTCFullYear(), baseDate.getUTCMonth(), baseDate.getUTCDate(), h, m, 0);
            }
            return Date.UTC(baseDate.getUTCFullYear(), baseDate.getUTCMonth(), baseDate.getUTCDate());
        };
        const baseMs = getRoundBaseMs();

        const updatedRegistrations = viewEventData.registrations.map((reg: any) => {
            if (!reg.teeTimes?.length) return reg;
            return {
                ...reg,
                teeTimes: reg.teeTimes.map((tt: any) => {
                    if (tt.round !== roundValue || !tt.teeTime) return tt;
                    const storedMs = new Date(tt.teeTime).getTime();
                    const slotIndex = Math.round((storedMs - baseMs) / (oldGap * 60000));
                    const newTeeTimeMs = baseMs + slotIndex * newGap * 60000;
                    return { ...tt, teeTime: new Date(newTeeTimeMs).toISOString() };
                }),
            };
        });

        setViewEventData({ ...viewEventData, registrations: updatedRegistrations });
    }, [viewEvent?.rounds[roundValue - 1]?.teeTimeGap]);


    /* useEffect(() => {

        if (isLoading || !viewEvent) return;
        const updatedRounds = JSON.parse(JSON.stringify(viewEvent.rounds));

        // Calculate smallest power of 2 greater than playerCap
        const nextPowerOf2 = Math.pow(2, Math.ceil(Math.log2(viewEvent.playerCap)));

        updatedRounds[roundValue - 1].matchRegionRounds = Math.log2(nextPowerOf2/viewEvent.rounds[roundValue - 1].matchRegions); //Math.ceil(Math.log2(viewEvent.playerCap/viewEvent.rounds[roundValue - 1].matchRegions));
        setViewEvent({...viewEvent, rounds: updatedRounds});
    }, [viewEvent?.rounds[roundValue - 1]?.matchRegions, isLoading]); */

    const combineDateAndTime = (roundDateString: string, startingTime: string) => {
        // Parse the roundDate string into a Date object
        const roundDate = new Date(roundDateString);
    
        // Split the starting time into hours and minutes
        const [hours, minutes] = startingTime.split(':').map(Number);
    
        // Set the hours and minutes on the roundDate
        roundDate.setUTCHours(hours); // Use setUTCHours to maintain UTC context
        roundDate.setUTCMinutes(minutes); // Use setUTCMinutes to maintain UTC context
    
        return roundDate; // Return the updated Date object
    };

    const validateRounds = async (rounds: any) => {
    
        const validatedRounds = rounds.map((round: any) => {
            const validatedRound = {
                ...round,
                targetRoundLength: Number(round.targetRoundLength),
                teeTimeGap: Number(round.teeTimeGap),
                regPerTeeTime: Number(round.regPerTeeTime),
                usePoints: typeof round.usePoints === 'string' 
                    ? round.usePoints?.toLowerCase() === 'true'
                    : Boolean(round.usePoints),
                pointSystem: (typeof round.usePoints === 'string' 
                    ? round.usePoints?.toLowerCase() === 'true'
                    : Boolean(round.usePoints)) ? round.pointSystem : null,
                holePoints: (typeof round.usePoints === 'string' 
                ? round.usePoints?.toLowerCase() === 'true'
                : Boolean(round.usePoints)) ? round.holePoints : [],
                mixedPlayFormats: round.playFormat?.toLowerCase() === 'mixed' ? round.mixedPlayFormats : []
    
            }
    
            return validatedRound;
        
        });
    
        return validatedRounds;
    }

    const updateEvent = async (event: any) => {
        const updatedEventData = eventData.map((existingEvent: any) => 
            existingEvent._id === event._id ? { ...existingEvent, ...event } : existingEvent
        );
        setEventData(updatedEventData);
        setViewEvent(event);
        
    }

    // Updates rounds state in the editor
    function UpdateRoundState(roundIndex: number, keyName: string, keyValue: any, keyIDName?: string, keyIDValue?: any) {
        if (!viewEvent) return;
        const updatedRounds = JSON.parse(JSON.stringify(viewEvent.rounds));
        updatedRounds[roundIndex][keyName] = keyValue;
        if (keyIDName) {
            updatedRounds[roundIndex][keyIDName] = keyIDValue;
        }
        setViewEvent({ ...viewEvent, rounds: updatedRounds });

        if (keyName === 'startHole') {
            const roundCfg = updatedRounds[roundIndex];
            if (!effectiveRoundBool(roundCfg?.shotgunStart) && !effectiveRoundBool(roundCfg?.splitTees)) {
                const clampedHole = clampStartHole(
                    keyValue,
                    Number(roundCfg?.totalHoles) || 18,
                );
                setViewEventData((prev) => {
                    const patch = applyUniformStartHoleToViewEventData(
                        prev,
                        roundIndex + 1,
                        keyValue,
                        roundCfg,
                    );
                    return Object.keys(patch).length === 0 ? prev : { ...prev, ...patch };
                });
                setForceUpdate(!forceUpdate);

                const eventId = viewEvent._id;
                if (eventId && auth.user?.profile?.sub) {
                    void api
                        .post('/tb/SyncRoundStartingHoleToEventScores', {
                            eventID: String(eventId),
                            userID: auth.user.profile.sub,
                            roundNumber: roundIndex + 1,
                            startingHole: clampedHole,
                        })
                        .catch((err: unknown) =>
                            console.error('[SyncRoundStartingHoleToEventScores]', err),
                        );
                }
            }
        }
    }

    async function UpdateRoundJSONItem(roundIndex: number, keyName: string, subName: string, value: any) {
        if (!viewEvent) return;
        const updatedRounds = JSON.parse(JSON.stringify(viewEvent.rounds));
        const jsonItem = updatedRounds[roundIndex][keyName] || '[]';
        const existingItem = jsonItem.find((item: any) => item.name === subName) || [];
        let updatedJSONItem = jsonItem;

        if (value === null && existingItem) {
            updatedJSONItem = jsonItem.filter((item: any) => item.name !== subName);
        } else if (existingItem && typeof existingItem === 'object' && 'name' in existingItem) {
            updatedJSONItem.value = jsonItem.filter((item: any) => item.name === subName)[0].value = value;
        } else {
            updatedJSONItem.push({name: subName, value: value});
        }

        updatedRounds[roundIndex][keyName] = updatedJSONItem;
        setViewEvent({ ...viewEvent, rounds: updatedRounds });
    }

    async function UpdateEventJSONItem(roundIndex: number, keyName: string, subName: string, value: any, itemIndex?: number) {
        if (!viewEvent) return;
        const updatedEvent = JSON.parse(JSON.stringify(viewEvent)) as typeof viewEvent;
        const rawKeyValue = updatedEvent[keyName];
        // #region agent log
        const logBefore = { keyName, itemIndex, subName, value, rawSample: Array.isArray(rawKeyValue) ? rawKeyValue.slice(0,5) : rawKeyValue };
        console.log('[DEBUG] UpdateEventJSONItem BEFORE', logBefore);
        fetch('http://127.0.0.1:7717/ingest/ce1ec9c7-ce84-4aa0-b042-8693885fd025',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'918490'},body:JSON.stringify({sessionId:'918490',location:'TournamentEditor.tsx:UpdateEventJSONItem',message:'eventInfo BEFORE',data:Object.assign(logBefore,{rawType:typeof rawKeyValue,rawIsArray:Array.isArray(rawKeyValue),rawKeys:rawKeyValue&&typeof rawKeyValue==='object'?Object.keys(rawKeyValue):null}),timestamp:Date.now(),hypothesisId:'H1'})}).catch(()=>{});
        // #endregion
        const jsonItem = Array.isArray(updatedEvent[keyName]) ? [...updatedEvent[keyName]] : [];

        let updatedJSONItem: any[];
        if (value === null) {
            // Delete: use index when provided (avoids wrong-item deletion with duplicate names)
            if (typeof itemIndex === 'number' && itemIndex >= 0 && itemIndex < jsonItem.length) {
                const removed = jsonItem.splice(itemIndex, 1);
                updatedJSONItem = jsonItem;
                // #region agent log
                const logDel = { itemIndex, removed, resultLength: updatedJSONItem.length, resultSample: updatedJSONItem.slice(0,5) };
                console.log('[DEBUG] UpdateEventJSONItem delete by index', logDel);
                fetch('http://127.0.0.1:7717/ingest/ce1ec9c7-ce84-4aa0-b042-8693885fd025',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'918490'},body:JSON.stringify({sessionId:'918490',location:'TournamentEditor.tsx:UpdateEventJSONItem',message:'delete by index',data:logDel,timestamp:Date.now(),hypothesisId:'H2'})}).catch(()=>{});
                // #endregion
            } else {
                const existingItem = jsonItem.find((item: any) => item.name === subName);
                updatedJSONItem = existingItem ? jsonItem.filter((item: any) => item.name !== subName) : jsonItem;
            }
        } else {
            const existingItem = jsonItem.find((item: any) => item.name === subName);
            if (existingItem && typeof existingItem === 'object' && 'name' in existingItem) {
                updatedJSONItem = jsonItem.map((item: any) =>
                    item.name === subName ? { ...item, value } : item
                );
            } else {
                updatedJSONItem = [...jsonItem, { name: subName, value }];
            }
        }

        updatedEvent[keyName] = updatedJSONItem;
        // #region agent log
        const logAfter = { finalLength: updatedJSONItem.length, finalItems: updatedJSONItem };
        console.log('[DEBUG] UpdateEventJSONItem AFTER', logAfter);
        fetch('http://127.0.0.1:7717/ingest/ce1ec9c7-ce84-4aa0-b042-8693885fd025',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'918490'},body:JSON.stringify({sessionId:'918490',location:'TournamentEditor.tsx:UpdateEventJSONItem',message:'eventInfo AFTER setViewEvent',data:logAfter,timestamp:Date.now(),hypothesisId:'H3'})}).catch(()=>{});
        // #endregion
        setViewEvent(updatedEvent);
    }

    // Saves event and round data to the database
    async function HandleEventSave() {
        if (!viewEvent) return;
        const saveViewEvent = JSON.parse(JSON.stringify(viewEvent));

        const validatedRounds = await validateRounds(JSON.parse(JSON.stringify(viewEvent.rounds)));
        
        saveViewEvent.rounds = validatedRounds;
        const original = originalViewEvent.current;
        console.log('Ref', original);

        // Updates event data if it has changed
        if (original != null && original !== saveViewEvent) {

            // Create object to store changed fields
            const changedFields: any = {};
            
            // Compare each key in saveViewEvent with originalViewEvent
            Object.keys(saveViewEvent).forEach((key) => {

                // Skip if key doesn't exist in original
                if (!Object.prototype.hasOwnProperty.call(original, key)) {

                    changedFields[key] = viewEvent?.[key as keyof typeof viewEvent];
                    return;
                }

                // Compare values and add to changedFields if different
                if (JSON.stringify(original[key as keyof typeof original]) !== JSON.stringify(saveViewEvent[key])) {
                    changedFields[key] = saveViewEvent[key];
                }

            });

            if (Object.prototype.hasOwnProperty.call(changedFields, 'useRivalry')) {
                if (changedFields.useRivalry) {
                    changedFields.rivalry = resolveRivalryConfig({
                        useRivalry: true,
                        rivalry: changedFields.rivalry ?? saveViewEvent.rivalry,
                    });
                } else {
                    changedFields.rivalry = null;
                }
            }

            if (Object.keys(changedFields).length > 0) {
                const payloadToSend = convertDatesToUTC(changedFields as Record<string, unknown>);
                await api.post('/tb/UpdateEvent', {
                    _id: saveViewEvent._id,
                    userID: auth.user?.profile.sub,
                    ...payloadToSend
                })

                .then((res: any) => {
                    console.log(res);
                })
                .catch((err: any) => {
                    console.error(err);
                });
            }

            // Update the original reference to match current state
            originalViewEvent.current = {...saveViewEvent};
        } 

        handleRegistrationUpdate();

        // Resets edit mode and forces a refresh of the event editor item displays
        updateEvent(JSON.parse(JSON.stringify(saveViewEvent))); // Updates event data in the event manager
        setEditMode(false);

        originalViewEvent.current = JSON.parse(JSON.stringify(saveViewEvent));

    }

    const handleRegistrationUpdate = async () => {

        const saveRegistrations: any[] = [];
        const updatedRegistrations = JSON.parse(JSON.stringify(viewEventData.registrations));

        if (JSON.stringify(originalRegistrations.current) !== JSON.stringify(updatedRegistrations)) {

            originalRegistrations.current.forEach((registration: any) => {
                const singleUpdatedRegistration = updatedRegistrations.find((reg: any) => reg._id === registration._id);
                
                if (JSON.stringify(singleUpdatedRegistration) !== JSON.stringify(registration)) {
                    // Create a filtered registration without hasUserID and hasEventScoreID
                    const { hasUserID, hasEventScoreID, ...filteredRegistration } = singleUpdatedRegistration;
                    saveRegistrations.push(filteredRegistration);
                }
            });

            api.post('/tb/UpdateRegistrations', {
                user_userID: auth.user?.profile.sub,
                registrations: saveRegistrations
            })
            .then((res: any) => {
                console.log(res);
            })
            .catch((err: any) => {
                console.error(err);
            });  
        }

        originalRegistrations.current = JSON.parse(JSON.stringify(viewEventData.registrations));
    }

    const addSideGame = (value: any, sideGameIndex: number) => {
        if (!viewEvent) return;
        const round = viewEvent.rounds?.[roundValue - 1];
        if (!round) return;
        const newViewEvent = JSON.parse(JSON.stringify(viewEvent));
        const sideGamesOption = optionsLoaded.find((item: any) => item.keyName === 'SideGames') as { options: Array<{ type: string; dataModel: Record<string, unknown> }> } | undefined;
        const updatedSideGames = newViewEvent.rounds[roundValue - 1].sideGames;
        const template = sideGamesOption?.options?.find((item: any) => item.type === value.type)?.dataModel ?? {};
        updatedSideGames[sideGameIndex] = { type: value.type, ...template };
        newViewEvent.rounds[roundValue - 1].sideGames = updatedSideGames;
        setViewEvent(newViewEvent);
    }

    const addChallenge = (value: any, challengeIndex: number) => {
        if (!viewEvent) return;
        const round = viewEvent.rounds?.[roundValue - 1];
        if (!round) return;
        const newViewEvent = JSON.parse(JSON.stringify(viewEvent));
        const challengesOption = optionsLoaded.find((item: any) => item.keyName === 'Challenges') as { options: Array<{ type: string; dataModel: Record<string, unknown> }> } | undefined;
        const updatedChallenges = newViewEvent.rounds[roundValue - 1].challenges;
        const template = challengesOption?.options?.find((item: any) => item.type === value.type)?.dataModel ?? {};
        updatedChallenges[challengeIndex] = { type: value.type, ...template };
        console.log('Updated Challenges', updatedChallenges);
        newViewEvent.rounds[roundValue - 1].challenges = updatedChallenges;
        setViewEvent(newViewEvent);
    }
    
    if (!viewEvent) return null;
    const event = viewEvent;

    const headerHeight = document.getElementById('event-view-header')?.clientHeight;
    //const mainBodyHeight = document.getElementById('event-view-main-body')?.clientHeight;

    return (
        <Stack id='event-view-container'
            direction={'column'} spacing={0}
            sx={{ 
                width: '100%', 
                height: '100%', 
                overflow: 'hidden'
            }}>
            <Stack id='event-view-header'
                direction={'row'} spacing={0.5} sx={{ width: '100%', flexShrink: 0, alignItems: 'center', justifyContent: 'flex-start',
                    padding: '10px 20px', backgroundColor: 'sepia.background',
                    borderBottom: `1px solid ${appTheme.palette.primary.main}`, boxSizing: 'border-box' }}>
                {/* <IconButton
                    disabled={editMode}
                    sx={{ padding: 0, borderRadius: 1, width: 150 * factor, justifyContent: 'flex-start' }}
                    onClick={() => {
                        setEventTab('details');
                        setEventTabValue(0);
                        setRoundValue(1);
                        setRoundTabValue(0);
                        if (viewGroup) {
                            setAppSection('groups');
                            setViewEvent(null);
                            setGroupTabValue(0);
                        } else {
                            returnToManager(null);
                        }
                    }}
                >
                    <Stack direction={'row'} spacing={0} sx={{ alignItems: 'center', justifyContent: 'flex-start' }}>
                        <ArrowBackIosNewIcon color='primary' sx={{ height: 12, padding: 0 }} />
                        <Typography variant='caption' color='primary.main' sx={{ alignItems: 'left' }}>
                            {viewGroup ? `Back to ${viewGroup.groupName}` : 'Back to Event List'}
                        </Typography>
                    </Stack>
                </IconButton> */}
                <Stack id='event-view-main-header'
                    direction={'row'} spacing={3} sx={{ width: '100%', padding: '0px 10px', display: 'flex', justifyContent: 'space-between',
                    minHeight: 35, alignItems: 'center' }}>

                    <Stack direction={'row'} spacing={1} sx={{ flex: 1,alignItems: 'center', justifyContent: 'flex-start' }}>
                        <Typography variant='h6' sx={{ fontWeight: 500 }}>{viewEvent?.eventName}</Typography>
                        <Typography sx={{ fontSize: 12*factor*2, flexShrink: 0 }}>@</Typography>
                        <Typography variant='h6' sx={{ fontWeight: 500 }}>{viewEvent?.courseName}</Typography>
                    </Stack>

                    <Stack direction={'row'} spacing={1} sx={{ ml: 'auto', alignItems: 'center', flexShrink: 0 }}>
                        {!editMode ? (
                            <Button
                                variant='outlined'
                                color='primary'
                                startIcon={<EditIcon fontSize="small" />}
                                sx={{ height: 30 * factor }}
                                onClick={() => setEditMode(true)}
                            >
                                Edit
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    sx={{ width: 100 * factor, height: 30 * factor }}
                                    onClick={() => HandleEventSave()}
                                >
                                    Save
                                </Button>
                                <Button
                                    variant='outlined'
                                    color='error'
                                    sx={{ width: 100 * factor, height: 30 * factor }}
                                    onClick={() => {
                                        setViewEvent(originalViewEvent.current);
                                        setViewEventData(originalViewEventData.current);
                                        setEditMode(false);
                                        setForceUpdate(!forceUpdate);
                                    }}
                                >
                                    Close
                                </Button>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Stack>

            
            <Stack id='event-view-body'
                direction={'row'} spacing={0} sx={{ padding: '0px 0px', width: '100%', flex: 1, minHeight: 0,
                    alignItems: 'stretch'/* , border: '1px solid green' */ }}>

                <Box id='tournament-builder-loading-box'
                    sx={{ 
                        display: isLoading ? 'flex' : 'none', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100vh',
                        width: '100%',
                        transition: 'opacity 0.5s ease-in-out',
                        opacity: isLoading ? 1 : 0,
                        pointerEvents: isLoading ? 'auto' : 'none'
                    }}
                    className={isLoading ? 'loading-transition' : 'loading-hidden'}
                    >
                    <Typography variant="h6">Loading...</Typography>
                    <CircularProgress />
                </Box>

                {isLoading ? null :
                    (<>
                    {/* Sidebar commented out for all editor tabs — Core Items covers owner/dates/season on Event Details; Delete needs a future home. May repurpose later.
                    <Stack id='event-view-sidebar'
                        direction={'column'} spacing={editMode ? 1.25 : 2} justifyContent={'space-between'}
                            sx={{ width: 'max-content', height: `calc(100% - 60px)`, minWidth: 150, maxWidth: 200, backgroundColor: 'sepia.background',
                        marginLeft: '1px',
                        padding: '50px 20px 10px 20px',
                        borderRight: `1px solid`,
                        borderColor: 'sepia.extraLight',
                        }}>
                            <Stack direction={'column'} spacing={1}>
                                <EventItemDisplay orient='column'  label='Owner' keyName='ownerName' flipped={false} />

                                <EventItemDisplay orient='column' label='Start Date' keyName='startDate' flipped={false} />         

                                <EventItemDisplay orient='column' label='End Date' keyName='endDate' flipped={false} />

                                <EventItemDisplay orient='column' label='Season / Year' keyName='year' flipped={false} />

                            </Stack>
                            <Stack direction={'column'} spacing={1}>
                                <StyledIconButton 
                                    appTheme={appTheme} 
                                    label='Delete' 
                                    factor={factor} 
                                    color='error' 
                                    icon={<DeleteIcon sx={{ fontSize: 16 }} />} 
                                    onClick={() => deleteEvent()} />
                            </Stack>
                    </Stack>
                    */}
                    <Stack id='event-view-main'
                        direction={'column'} sx={{ width: '100%', height: '100%', minHeight: 0 }}>

                        <Stack id='event-view-main-body'
                            direction={'column'} sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}>

                            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
                                overflow: 'hidden', minHeight: 0, flex: 1 }}>
                                {eventTabValue >= 4 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '5px 20px',
                                        height: 50, flexShrink: 0, borderBottom: `1px solid ${appTheme.palette.divider}` }}>
                                    <Stack direction='row' spacing={2} flex={1} padding={'0px 40px'} alignItems='center' justifyContent='flex-end'>
                                        <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Round</Typography>
                                        <Stack direction='row' spacing={0} padding={'0px 0px'} alignItems='center' justifyContent='flex-end'>
                                            {Array.from({ length: Number(event.totalRounds) }, (_, index) => index + 1).map((number) => (
                                                <RoundButton 
                                                    key={`${roundValue}-${number}`} 
                                                    number={number} 
                                                    roundValue={roundValue} 
                                                    editMode={editMode} 
                                                    appTheme={appTheme}
                                                    factor={factor} 
                                                    setRoundValue={setRoundValue} 
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>
                                </Box>
                                )}
                                
                                <EventDetailsTab appDims={appDims} compHeights={compHeights} eventDataLayout={eventDataLayout} setEventDataLayout={setEventDataLayout} eventLocked={hasAnyPlayerStartedAnyRound(viewEventData.scores)} />
                                <FlightingTab appDims={appDims} compHeights={compHeights} />

                                <TabPanel value={eventTabValue} index={2} appDims={appDims} compHeights={compHeights} factor={factor} fillAvailable> {/* Information Tab */}
                                <Stack direction='column' spacing={1} sx={{ height: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                    <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between' sx={{ borderBottom: '1px dotted lightgray', flexShrink: 0 }}>
                                        <Typography variant='subtitle2' color='primary' sx={{ fontWeight: 500 }}>Event Information</Typography>
                                        <PushNotificationDialog />
                                    </Stack>
                                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                        <EventInfoEditors
                                            generalInfo={viewEvent?.eventGeneralInfo ?? ''}
                                            firstTeeInfo={viewEvent?.eventFirstTeeInfo ?? ''}
                                            onGeneralChange={(html) => setViewEvent({ ...viewEvent, eventGeneralInfo: html })}
                                            onFirstTeeChange={(html) => setViewEvent({ ...viewEvent, eventFirstTeeInfo: html })}
                                            readOnly={!editMode}
                                        />
                                    </Box>
                                </Stack>
                                </TabPanel>
                                <TabPanel value={eventTabValue} index={3} appDims={appDims} compHeights={compHeights} factor={factor} fillAvailable> {/* Registrations Tab */}
                                    <RegistrationTab
                                        compHeights={compHeights}
                                        appDims={appDims}
                                        //handleRegistrationUpdate={handleRegistrationUpdate}
                                        //registrationRef={originalRegistrations.current}
                                    />
                                </TabPanel>
                                <RoundDetailsTab appDims={appDims} compHeights={compHeights} roundDataLayout={roundDataLayout} setRoundDataLayout={setRoundDataLayout} UpdateRoundState={UpdateRoundState} roundLocked={hasAnyPlayerStartedRound(viewEventData.scores, roundValue)} />
                                <TabPanel value={eventTabValue} index={5} appDims={appDims} compHeights={compHeights} factor={factor}  >
                                    {(() => {
                                        const currentRound = event?.rounds?.[roundValue - 1];
                                        if (!currentRound) {
                                            return (
                                                <Stack direction={'row'} padding={'0px 40px'} alignItems='center' justifyContent='center'
                                                    sx={{ height: compHeights.appUsableHeight - 195*factor }}>
                                                    <Typography color='text.secondary'>No round data available for this round.</Typography>
                                                </Stack>
                                            );
                                        }
                                        const challenges = currentRound.challenges ?? [];
                                        const sideGames = currentRound.sideGames ?? [];
                                        return (
                                    <Stack direction={'row'} spacing={10} flex={1} padding={'0px 40px'} justifyContent='space-between'
                                        sx={{ height: compHeights.appUsableHeight - 195*factor, overflowY: 'auto' }}
                                    >

                                        <Stack direction='column' spacing={editMode ? 1.25 : 1} flex={1}>
                                            <Stack direction={'column'} spacing={1} sx={{ borderTop: `1px dashed ${appTheme.palette.divider}`,
                                                paddingTop: '5px'
                                            }}>
                                                <Stack direction={'row'} spacing={1} alignItems='center' justifyContent='space-between'>
                                                    <Typography variant='subtitle1' sx={{ fontWeight: 500, width: 150*factor }}>
                                                        Challenges
                                                    </Typography>
                                                    <IconButton 
                                                        disabled={!editMode}
                                                        sx={{
                                                            width: 150,
                                                            height: 30*factor,
                                                            padding: 0,
                                                            borderRadius: 1,
                                                            color: !editMode ? 'gray' : 'primary.main'
                                                        }}
                                                        onClick={() => {
                                                        const newChallenges = JSON.parse(JSON.stringify(challenges));
                                                        newChallenges.push({name: '', type: ''});
                                                        setViewEvent({...event,
                                                            rounds: [
                                                                ...event.rounds.slice(0, roundValue-1),
                                                                {...currentRound,
                                                                    challenges: newChallenges}, ...event.rounds.slice(roundValue)]});
                                                    }}>
                                                        <AddIcon /> 
                                                        <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Add Challenge</Typography>
                                                    </IconButton>
                                                </Stack>
                                                
                                                {challenges.map((challenge: any, index: number) => {
                                                    const challengesOptions = (optionsLoaded?.find((item: any) => item.keyName === 'Challenges') as { options: Array<{ type: string }> } | undefined)?.options ?? [];
                                                    return (
                                                        <Stack key={index} direction={'row'} spacing={3} width={'100%'} padding={'0px 0px'}
                                                        alignItems={'center'}
                                                        >
                                                            <StyledAutocompleteInput
                                                                label={'Challenge'}
                                                                disableClearable={true}
                                                                disabled={!editMode}
                                                                factor={factor}
                                                                appTheme={appTheme}
                                                                width={180*factor}
                                                                value={challengesOptions.find((item: any) => item.type === challenge.type) || null}
                                                                getOptionLabel={(option: any) => option.type}
                                                                options={challengesOptions}
                                                                onChange={(event, value) => {
                                                                    addChallenge(value, index);
                                                                }}
                                                            />
                                                            <ChallengeOptions challengeIndex={index} />
                                                        </Stack>
                                                    );
                                                })}
                                                
                                            </Stack>

                                            
                                            <Stack direction={'column'} spacing={1} sx={{ borderTop: `1px dashed ${appTheme.palette.divider}`,
                                                paddingTop: '5px'
                                            }}>
                                                <Stack direction={'row'} spacing={1} alignItems='center' justifyContent='space-between'>
                                                    <Typography variant='subtitle1' sx={{ fontWeight: 500, width: 150*factor }}>
                                                        Side Games
                                                    </Typography>
                                                    <IconButton 
                                                        disabled={!editMode}
                                                        sx={{
                                                            width: 150,
                                                            height: 30*factor,
                                                            padding: 0,
                                                            borderRadius: 1,
                                                            color: !editMode ? 'gray' : 'primary.main'
                                                        }}
                                                        onClick={() => {
                                                        const newSideGames = JSON.parse(JSON.stringify(sideGames));
                                                        newSideGames.push({name: '', type: ''});
                                                        setViewEvent({...event,
                                                            rounds: [
                                                                ...event.rounds.slice(0, roundValue-1),
                                                                {...currentRound,
                                                                    sideGames: newSideGames}, ...event.rounds.slice(roundValue)]});
                                                    }}>
                                                        <AddIcon /> 
                                                        <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Add Side Game</Typography>
                                                    </IconButton>
                                                </Stack>
                                                {sideGames.map((sideGame: any, index: number) => (
                                                    
                                                    <Stack key={index} direction={'row'} spacing={3} width={'100%'} padding={'0px 0px'}
                                                    alignItems={'center'}
                                                    >
                                                        <StyledAutocompleteInput
                                                            label={'Side Game Model'}
                                                            disableClearable={true}
                                                            disabled={!editMode}
                                                            factor={factor}
                                                            appTheme={appTheme}
                                                            width={180*factor}
                                                            value={(optionsLoaded?.find((item: any) => item.keyName === 'SideGames') as { options: Array<{ type: string }> } | undefined)?.options
                                                                ?.find((item: any) => item.type === sideGame.type) || null}
                                                            getOptionLabel={(option: any) => option.type}
                                                            options={(optionsLoaded?.find((item: any) => item.keyName === 'SideGames') as { options: Array<{ type: string }> } | undefined)?.options
                                                                ?.filter((item: any) => !sideGames
                                                                .some((sg: any) => sg.type === item.type)) || []}
                                                            onChange={(event, value) => {
                                                                addSideGame(value, index);
                                                            }}
                                                        />
                                                        <SideGameOptions sideGameIndex={index} />
                                                    </Stack>
                                                   
                                                ))}
                                                
                                            </Stack>
                                        </Stack>

                                        </Stack>
                                        );
                                    })()}
                                            
                                    </TabPanel>
                                    <TabPanel value={eventTabValue} index={6} appDims={appDims} compHeights={compHeights} factor={factor} fillAvailable>
                                        <Stack direction={'row'} spacing={0} sx={{ flex: 1, width: '100%', height: '100%', minHeight: 0 }}>
                                            <EventTeeSheet 
                                                roundLocked={hasAnyPlayerStartedRound(viewEventData.scores, roundValue)}
                                            />
                                        </Stack>
                                    </TabPanel>
                                
                            </Box>
                        </Stack>
                        <Divider sx={{ margin: 0, padding: 0 }} />
                        
                        <Stack direction={'row'} spacing={2}
                        sx={{ width: `calc(100% - 60px)`, height: 50*factor, flexShrink: 0, justifyContent: 'space-between', padding: '0px 20px', alignItems: 'center' }}>
                            {eventTabValue === 6 && (
                                <TeeSheetPopout compHeights={compHeights}/>
                            )}
                            {eventTabValue === 3 && (
                                <Stack direction={'row'} spacing={0} 
                                sx={{ height: 60*factor, padding: '0px 0px', alignItems: 'center' }}>
                                    <RegistrationPopout
                                        compHeights={compHeights}
                                        appDims={appDims}
                                        gridHeight={compHeights.appUsableHeight - 100}
                                        gridWidth={Number(`calc(80vw-30px)`)}
                                        //handleRegistrationUpdate={handleRegistrationUpdate}
                                        //registrationRef={originalRegistrations.current}
                                    />
                                    <RegistrationFormPopout
                                        compHeights={compHeights}
                                        appDims={appDims}
                                        saveEvent={() => HandleEventSave()}
                                    />
                                    <RegistrationDisplayInfoPopout
                                        compHeights={compHeights}
                                        appDims={appDims}
                                        saveEvent={() => HandleEventSave()}
                                    />
                                </Stack>
                            )}

                            <Stack direction={'row'} spacing={2}
                            sx={{ display: 'flex', height: 50*factor, justifyContent: 'flex-end', padding: '0px 0px', alignItems: 'center' }}>
                                
                                <Box minHeight={60*factor}></Box>
                                {viewEventData?.registrations?.length > 0 &&
                                    <Button variant='outlined' color='primary' sx={{ width: 'max-content', height: 30*factor }}
                                        onClick={async () => {
                                            await api.post('/tb/SyncRegistrationUsers', {
                                                registrations: viewEventData.registrations,
                                                user_userID: auth.user?.profile.sub
                                            })
                                            .then((res: any) => {

                                                console.log('Res', res.data);
                                                setViewEventData({...viewEventData, registrations: res.data});
                                                alert('Users Synced');
                                                console.log(res);
                                            })
                                        }}
                                    >Sync Users
                                    </Button>
                                }
                                {allTeeTimesValid && !editMode && (
                                    <Tooltip title={hasAnyPlayerStartedAnyRound(viewEventData.scores) ? 'Update Scores' : 'Create Scores'}>
                                        <Button variant='outlined' color='primary' sx={{ width: 'max-content', height: 30*factor }}
                                            startIcon={hasAnyPlayerStartedAnyRound(viewEventData.scores) ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                            onClick={async () => {

                                                const eventScores = CreateEventScores({
                                                    auth: { user: auth.user ? { profile: auth.user.profile } : undefined },
                                                    viewEvent: event,
                                                    viewEventData
                                                });

                                                await api.post('/tb/CreateEventScores', {
                                                    eventScores: eventScores,
                                                    userID: auth.user?.profile?.sub,
                                                    eventID: event._id
                                                })
                                                .then(async (res: any) => {
                                                    const [regRes, scoresRes] = await Promise.all([
                                                        api.get('/tb/GetEventRegistrations', { params: { _id: event._id } }),
                                                        api.get('/tb/GetEventScores', { params: { _id: event._id, referenceDate: '1970-01-01' } })
                                                    ]);
                                                    setViewEventData({ ...viewEventData, registrations: regRes.data, scores: scoresRes.data });
                                                    setForceUpdate(!forceUpdate);
                                                    const deletedOrphans = res.data?.deletedOrphanCount ?? 0;
                                                    const retainedOrphans = res.data?.retainedOrphanCount ?? 0;
                                                    let message = hasAnyPlayerStartedAnyRound(scoresRes.data) ? 'Scores Updated' : 'Scores Created';
                                                    if (deletedOrphans > 0 || retainedOrphans > 0) {
                                                        const parts: string[] = [];
                                                        if (deletedOrphans > 0) {
                                                            parts.push(`${deletedOrphans} unstarted orphan${deletedOrphans === 1 ? '' : 's'} removed`);
                                                        }
                                                        if (retainedOrphans > 0) {
                                                            parts.push(`${retainedOrphans} started orphan${retainedOrphans === 1 ? '' : 's'} kept in history`);
                                                        }
                                                        message += ` (${parts.join('; ')})`;
                                                    }
                                                    alert(message);
                                                })
                                                .catch((err: any) => {
                                                    console.error(err);
                                                });
                                                
                                            }}
                                        >Scores
                                        </Button>
                                    </Tooltip>
                                )}
                                {isDevEnvironment() && (
                                    <>
                                    <Tooltip title="Mock Scores">
                                        <Button variant='outlined' color='primary' sx={{ width: 'max-content', height: 30*factor }}
                                            startIcon={<ScienceIcon fontSize="small" />}
                                            onClick={async () => {
                                                if (!window.confirm('Fill hole scores with mock data for all players? This will overwrite existing scores.')) return;
                                                try {
                                                    await api.post('/ls/MockScores', {
                                                        eventID: event._id,
                                                        roundNumber: roundValue,
                                                        userID: auth.user?.profile?.sub
                                                    });
                                                    const [regRes, scoresRes] = await Promise.all([
                                                        api.get('/tb/GetEventRegistrations', { params: { _id: event._id } }),
                                                        api.get('/tb/GetEventScores', { params: { _id: event._id, referenceDate: '1970-01-01' } })
                                                    ]);
                                                    setViewEventData({ ...viewEventData, registrations: regRes.data, scores: scoresRes.data });
                                                    setForceUpdate(!forceUpdate);
                                                    alert('Mock scores applied');
                                                } catch (err: any) {
                                                    console.error(err);
                                                    alert(err?.response?.data?.error ?? 'Failed to mock scores');
                                                }
                                            }}
                                        >Scores
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Clear Scores">
                                        <Button variant='outlined' color='primary' sx={{ width: 'max-content', height: 30*factor }}
                                            startIcon={<ClearIcon fontSize="small" />}
                                            onClick={async () => {
                                                if (!window.confirm('Clear all hole scores for this round? This cannot be undone.')) return;
                                                try {
                                                    await api.post('/ls/ClearScores', {
                                                        eventID: event._id,
                                                        roundNumber: roundValue,
                                                        userID: auth.user?.profile?.sub
                                                    });
                                                    const [regRes, scoresRes] = await Promise.all([
                                                        api.get('/tb/GetEventRegistrations', { params: { _id: event._id } }),
                                                        api.get('/tb/GetEventScores', { params: { _id: event._id, referenceDate: '1970-01-01' } })
                                                    ]);
                                                    setViewEventData({ ...viewEventData, registrations: regRes.data, scores: scoresRes.data });
                                                    setForceUpdate(!forceUpdate);
                                                    alert('Scores cleared');
                                                } catch (err: any) {
                                                    console.error(err);
                                                    alert(err?.response?.data?.error ?? 'Failed to clear scores');
                                                }
                                            }}
                                        >Scores
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Delete All Event Scores">
                                        <Button variant='outlined' color='primary' sx={{ width: 'max-content', height: 30*factor }}
                                            startIcon={<DeleteIcon fontSize="small" />}
                                            onClick={async () => {
                                                if (!window.confirm('Delete all event scores for this event? Registrations will be unlinked. This cannot be undone.')) return;
                                                try {
                                                    const res = await api.post('/ls/DeleteAllEventScores', {
                                                        eventID: event._id
                                                    });
                                                    setViewEventData({ ...viewEventData, scores: [] });
                                                    setForceUpdate(!forceUpdate);
                                                    alert(`Deleted ${res.data?.deletedCount ?? 0} event score(s)`);
                                                } catch (err: any) {
                                                    console.error(err);
                                                    alert(err?.response?.data?.error ?? 'Failed to delete event scores');
                                                }
                                            }}
                                        >Scores
                                        </Button>
                                    </Tooltip>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>  
                </>)}              
            </Stack>
        </Stack>
    );
};
